import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ExternalLink, Trash2, RefreshCw, Power, PowerOff, Copy, ChevronDown, Loader2 } from 'lucide-react';
import { PrimaryLedgerCompany, PrimaryLedgerSyncStatus } from '../../types/primaryLedger';
import { Timestamp } from 'firebase/firestore';
import { useToast } from '../ui/use-toast';
import { primaryLedgerService } from '../../services/primaryLedgerService';
import { Progress } from '../ui/progress';

interface CompanyCardProps {
  company: PrimaryLedgerCompany;
  onDelete: (companyId: string, companyName: string) => void;
  onToggleSync: (companyId: string, currentStatus: boolean) => void;
  onManualSync: (companyId: string) => void;
  isLoading?: boolean;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onDelete,
  onToggleSync,
  onManualSync,
  isLoading = false
}) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [syncStatus, setSyncStatus] = useState<PrimaryLedgerSyncStatus | null>(null);
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [timeBasedProgress, setTimeBasedProgress] = useState(0);

  // Load expanded state and sync start time from localStorage on mount
  useEffect(() => {
    const savedExpandedState = localStorage.getItem(`company-expanded-${company.id}`);
    if (savedExpandedState !== null) {
      setIsExpanded(savedExpandedState === 'true');
    }

    // Restore sync start time if sync is in progress
    const savedSyncStartTime = localStorage.getItem(`sync-start-time-${company.id}`);
    if (savedSyncStartTime !== null) {
      setSyncStartTime(parseInt(savedSyncStartTime, 10));
    }
  }, [company.id]);

  // Subscribe to sync status for real-time updates
  useEffect(() => {
    const unsubscribe = primaryLedgerService.subscribeSyncStatus(
      company.id,
      (status) => {
        setSyncStatus(status);
        
        // Track sync start time
        if (status?.inProgress && !syncStartTime) {
          const startTime = Date.now();
          setSyncStartTime(startTime);
          // Persist to localStorage so progress survives page refresh
          localStorage.setItem(`sync-start-time-${company.id}`, String(startTime));
        } else if (!status?.inProgress && syncStartTime) {
          // Sync completed - reset and clear localStorage
          setSyncStartTime(null);
          setTimeBasedProgress(0);
          localStorage.removeItem(`sync-start-time-${company.id}`);
        }
      }
    );
    
    return () => unsubscribe();
  }, [company.id, syncStartTime]);

  // Time-based progress animation
  useEffect(() => {
    if (!syncStatus?.inProgress || !syncStartTime) {
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Date.now() - syncStartTime;
      const elapsedSeconds = elapsed / 1000;
      
      // First 5.5 minutes (330 seconds): 0% → 90%
      if (elapsedSeconds <= 330) {
        const progress = Math.min(90, (elapsedSeconds / 330) * 90);
        setTimeBasedProgress(Math.floor(progress));
      }
      // After 5.5 minutes: increment 1% per minute, cap at 99%
      else {
        const additionalMinutes = Math.floor((elapsedSeconds - 330) / 60);
        const progress = Math.min(99, 90 + additionalMinutes);
        setTimeBasedProgress(progress);
      }
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [syncStatus?.inProgress, syncStartTime]);

  // Animate to 100% on completion
  useEffect(() => {
    if (!syncStatus?.inProgress && timeBasedProgress > 0 && timeBasedProgress < 100) {
      // Quick animation to 100%
      const animationInterval = setInterval(() => {
        setTimeBasedProgress(prev => {
          if (prev >= 100) {
            clearInterval(animationInterval);
            return 100;
          }
          return Math.min(100, prev + 5);
        });
      }, 50);

      return () => clearInterval(animationInterval);
    }
  }, [syncStatus?.inProgress, timeBasedProgress]);

  const getSyncProgress = (): { percentage: number; message: string } | null => {
    if (!syncStatus?.inProgress && timeBasedProgress === 0) return null;
    
    // Use friendly message from backend
    let message = syncStatus?.friendlyMessage || 'Sync in progress...';
    
    // Add phase emoji if available
    if (syncStatus?.currentPhase) {
      const phaseEmoji = {
        'fetching': '📥',
        'uploading': '💾',
        'processing': '⚡',
        'complete': '✅'
      }[syncStatus.currentPhase];
      
      if (phaseEmoji && !message.startsWith(phaseEmoji)) {
        message = `${phaseEmoji} ${message}`;
      }
    }
    
    // Use time-based progress for smooth animation
    const percentage = timeBasedProgress;
    
    return { percentage, message };
  };

  const progress = getSyncProgress();

  // Save expanded state to localStorage
  const toggleExpand = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem(`company-expanded-${company.id}`, String(newState));
  };

  const formatTimestamp = (timestamp: Timestamp | null): string => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp.toMillis());
      
      // Format: "October 2, 2025 - 2AM EST"
      const options: Intl.DateTimeFormatOptions = {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        hour12: true,
        timeZoneName: 'short'
      };
      
      return date.toLocaleString('en-US', options).replace(' at ', ' - ');
    } catch (e) {
      return 'Invalid date';
    }
  };

  const formatSpreadsheetId = (id: string): string => {
    if (id.length <= 30) return id;
    return `${id.substring(0, 20)}...${id.substring(id.length - 10)}`;
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: `${label} copied to clipboard`,
    });
  };

  const SpreadsheetSection: React.FC<{
    label: string;
    spreadsheetId: string;
  }> = ({ label, spreadsheetId }) => (
    <Card className="p-3 bg-gray-50 dark:bg-gray-800/50">
      {/* Header: Title + Open link inline */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          {label}
        </h4>
        <a
          href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
          title="Open in Google Sheets"
        >
          Open
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* ID with copy button */}
      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
        <span className="font-mono truncate" title={spreadsheetId}>
          ID: {formatSpreadsheetId(spreadsheetId)}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(spreadsheetId, label);
          }}
          className="flex-shrink-0 p-1 hover:bg-gray-600/50 rounded transition-colors"
          title="Copy Spreadsheet ID"
        >
          <Copy className="h-3 w-3" />
        </button>
      </div>
    </Card>
  );

  return (
    <Card className="w-full bg-gray-50 dark:bg-gray-800/70 transition-shadow">
      {/* Header - Always visible, clickable */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-4 rounded-t-lg transition-colors"
        onClick={toggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpand();
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${company.companyName} details`}
      >
        <div className="flex items-center gap-3 flex-1">
          <ChevronDown
            className={`h-5 w-5 transition-transform duration-200 text-gray-500 dark:text-gray-400 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {company.companyName}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              Last sync: {formatTimestamp(syncStatus?.lastSync || null)}
              {syncStatus?.lastSyncSuccess === false && (
                <span className="ml-2 text-red-600 dark:text-red-400">⚠️ Failed</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {syncStatus?.inProgress && (
              <Badge variant="default" className="bg-blue-600 hover:bg-blue-700 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Syncing
              </Badge>
            )}
            {!syncStatus?.inProgress && company.syncEnabled && (
              <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                Sync Enabled
              </Badge>
            )}
            {!company.syncEnabled && (
              <Badge variant="secondary">
                Sync Disabled
              </Badge>
            )}
            {company.accessValidated && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700">
                Validated
              </Badge>
            )}
            <div className="flex gap-1 ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleSync(company.id, company.syncEnabled)}
                disabled={isLoading}
                title={company.syncEnabled ? 'Disable sync' : 'Enable sync'}
                className="h-8 w-8 p-0"
              >
                {company.syncEnabled ? (
                  <PowerOff className="h-4 w-4 text-orange-500" />
                ) : (
                  <Power className="h-4 w-4 text-green-500" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onManualSync(company.id)}
                disabled={isLoading || !company.syncEnabled}
                title="Trigger manual sync"
                className="h-8 w-8 p-0"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(company.id, company.companyName)}
                disabled={isLoading}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 w-8 p-0"
                title="Delete company"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
      </div>

      {/* Collapsible content */}
      {isExpanded && (
        <CardContent className="px-4 pb-4 pt-0 animate-in slide-in-from-top-2 duration-200">
          {/* Sync Progress Indicator */}
          {progress && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Sync in Progress
                </span>
              </div>
              <div className="relative">
                <Progress value={progress.percentage} className="h-2 mb-2 transition-all duration-300 ease-linear" />
                {/* Shimmer effect overlay */}
                {syncStatus?.inProgress && (
                  <div className="absolute inset-0 overflow-hidden rounded-full h-2 mb-2 pointer-events-none">
                    <div
                      className="absolute inset-0 -translate-x-full animate-shimmer"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
                      }}
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {progress.message} {progress.percentage > 0 && `(${progress.percentage}%)`}
              </p>
            </div>
          )}

          {/* Spreadsheet Connections */}
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Spreadsheet Connections
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          
          <SpreadsheetSection
            label="Sales & Profit History"
            spreadsheetId={company.salesProfitSpreadsheetId}
          />
          
          <SpreadsheetSection
            label="PPC Results"
            spreadsheetId={company.ppcResultsSpreadsheetId}
          />
          
              <SpreadsheetSection
                label="Margin History"
                spreadsheetId={company.marginHistorySpreadsheetId}
              />
            </div>
          </div>

          {/* Validation Errors */}
          {company.validationErrors && company.validationErrors.length > 0 && (
            <div className="mt-4 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
              <div className="font-semibold mb-1">Validation Errors:</div>
              <ul className="list-disc list-inside space-y-1">
                {company.validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Last sync: <span className="font-medium text-gray-900 dark:text-gray-100">{formatTimestamp(syncStatus?.lastSync || null)}</span>
              {syncStatus?.lastSyncSuccess === false && (
                <span className="ml-2 text-red-600 dark:text-red-400 font-medium">⚠️ Failed</span>
              )}
            </div>
            <Button
              onClick={() => onManualSync(company.id)}
              disabled={isLoading || !company.syncEnabled || syncStatus?.inProgress}
              size="sm"
              variant="outline"
              className="ml-4"
            >
              {syncStatus?.inProgress ? (
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
              )}
              {syncStatus?.inProgress ? 'Syncing...' : 'Manual Sync'}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default CompanyCard;